'use client';

import { useQuery } from '@tanstack/react-query';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { isStripeSubscriptionRequired } from '../../../config/subscriptionGate';
import { ROUTES } from '../../../constants/routes';
import { isShelterAdministrator, isSuperAdmin } from '../../../lib/rbac';
import { getMyShelterSubscription } from '../../shelters/api/shelters-api';
import { shelterKeys } from '../../shelters/queryKeys';
import {
  isAuthenticatedSession,
  useAuthSession,
} from '../hooks/useAuthSession';

const CHECKOUT_ALLOWLIST: string[] = [
  ROUTES.adminShelterSubscription,
  ROUTES.adminShelterSubscriptionCheckoutSuccess,
  ROUTES.adminSettings,
  ROUTES.adminSettingsAccount,
  ROUTES.adminSettingsSecurity,
  ROUTES.adminSettingsSubscription,
  ROUTES.adminSettingsBilling,
];

/**
 * Shelter Admins without a paid subscription are redirected to subscribe,
 * except on subscription/checkout and account settings routes.
 */
export function RequireActiveSubscription({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: session, isLoading: sessionLoading } = useAuthSession();
  const pathname = usePathname();
  const router = useRouter();

  const subscriptionRequired = isStripeSubscriptionRequired();

  const isShelterAdmin =
    isAuthenticatedSession(session) &&
    !isSuperAdmin(session.data.user.role) &&
    isShelterAdministrator(
      session.data.user.role,
      session.data.user.shelterRole,
    ) &&
    Boolean(session.data.user.shelterId);

  const subQ = useQuery({
    queryKey: shelterKeys.mySubscription(),
    queryFn: getMyShelterSubscription,
    enabled: isShelterAdmin && subscriptionRequired,
    staleTime: 30_000,
  });

  const onAllowlistedRoute =
    pathname != null && CHECKOUT_ALLOWLIST.includes(pathname);

  useEffect(() => {
    if (!subscriptionRequired) return;
    if (sessionLoading || !isShelterAdmin || subQ.isLoading) return;
    if (onAllowlistedRoute) return;
    const sub = subQ.data;
    if (sub?.requiresCheckout) {
      router.replace(ROUTES.adminShelterSubscription);
    }
  }, [
    subscriptionRequired,
    sessionLoading,
    isShelterAdmin,
    subQ.isLoading,
    subQ.data,
    onAllowlistedRoute,
    router,
  ]);

  if (!subscriptionRequired) {
    return <>{children}</>;
  }

  if (sessionLoading || !isAuthenticatedSession(session)) {
    return <>{children}</>;
  }

  if (!isShelterAdmin) {
    return <>{children}</>;
  }

  if (subQ.isLoading) {
    return (
      <div className='flex min-h-[30vh] items-center justify-center text-sm text-muted-foreground'>
        Checking subscription…
      </div>
    );
  }

  if (!onAllowlistedRoute && subQ.data?.requiresCheckout) {
    return (
      <div className='flex min-h-[30vh] items-center justify-center text-sm text-muted-foreground'>
        Redirecting to subscription…
      </div>
    );
  }

  return <>{children}</>;
}
